import {Chip, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip} from "@mui/material";
import {Fragment} from "react";
import {Page} from "../pages";
import {defineMessage, FormattedMessage} from "react-intl";
import {useHookClass} from "@dvrt/graphql-widgets";
import DataSource from "@/components/private-routing-destination/DataSource";
import {useBoolState} from "@/hooks";
import Loading from "@/components/breadcrumbs/Loading";
import PrivateRoutingDestinationForm from "@/components/private-routing-destination/PrivateRoutingDestinationForm";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    FormattedButton,
    FormActionButtons,
    FormattedFormRow,
    FormContainer,
    FormRowDivider,
    FormattedAlert,
} from "@dvrt/graphql-widgets";
import {useStyles} from "../call-group/members/styles";

const Content = ({dataSource}: {dataSource: DataSource}) => {
    const form = new PrivateRoutingDestinationForm(dataSource);
    const [enabled, enable, disable] = useBoolState(true);
    const {classes} = useStyles();

    const addNewDestination = () => {
        form.updateValues({destinations: [...form.values.destinations, {id: null, name: "", numberingPlans: []}]});
    };

    const removeDestination = (index: number) => {
        const newDestinations = structuredClone(form.values.destinations);
        newDestinations.splice(index, 1);
        form.updateValues({destinations: [...newDestinations]});
    };

    const updateValues = (index: number, value: string) => {
        const newDestinations = structuredClone(form.values.destinations);
        newDestinations[index].name = value;
        form.updateValues({destinations: [...newDestinations]});
    };

    async function submit() {
        disable();

        if (await form.submit()) {
            await dataSource.fetch();
            form.updateFormValues();
        }

        enable();
    }

    const addPrefix = (destinationIndex: number, e: any) => {
        const newPrefix = e.target.value.toString().replace(/ /g, "");
        if (
            (e.keyCode == 32 || e.keyCode == 13) &&
            newPrefix.length > 0 &&
            !form.values.destinations[destinationIndex].numberingPlans.find((p) => p.prefix == newPrefix)
        ) {
            const newDestinations = structuredClone(form.values.destinations);
            newDestinations[destinationIndex].numberingPlans.push({id: null, prefix: newPrefix});
            form.updateValues({destinations: [...newDestinations]});
            e.target.value = "";
        }
    };

    const removePrefix = (destinationIndex: number, prefixIndex: number) => {
        const newDestinations = structuredClone(form.values.destinations);
        newDestinations[destinationIndex].numberingPlans.splice(prefixIndex, 1);
        form.updateValues({destinations: [...newDestinations]});
    };

    const getError = (index: number): boolean => {
        if (form.errors.length) {
            return JSON.parse(form.errors[0].context.split('"context": ')[1].slice(0, -1)).includes(index);
        }
        return false;
    };

    return (
        <Page
            title={defineMessage({
                id: "privateRoutingDestinations.pageTitle",
                defaultMessage: "Custom routing destinations",
            })}
        >
            <FormContainer>
                {form.values.destinations.length ? (
                    <Table size={"small"} style={{marginBottom: "75px", width: "1000px"}}>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <FormattedMessage
                                        id="privateRoutingDestinations.table.name"
                                        defaultMessage="Name"
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage
                                        id="privateRoutingDestinations.table.prefixes"
                                        defaultMessage="Prefixes"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Tooltip
                                        arrow={true}
                                        placement={"top"}
                                        title={
                                            <FormattedMessage
                                                id="privateRoutingDestinations.buttons.tooltips.add"
                                                defaultMessage="Add new destination"
                                            />
                                        }
                                    >
                                        <span>
                                            <IconButton
                                                color="primary"
                                                size="small"
                                                onClick={addNewDestination}
                                                disabled={!enabled}
                                            >
                                                <FontAwesomeIcon icon="plus-square" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {form.values.destinations.map((dest, index) => (
                                <Fragment key={index}>
                                    <TableRow>
                                        <TableCell>
                                            <TextField
                                                variant="standard"
                                                value={dest.name}
                                                onChange={(ev) => updateValues(index, ev.target.value.toString())}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                {dest.numberingPlans.map((item, idx) => (
                                                    <Chip
                                                        style={{marginRight: "5px"}}
                                                        size="small"
                                                        label={item.prefix}
                                                        onDelete={() => removePrefix(index, idx)}
                                                        key={idx}
                                                    />
                                                ))}
                                            </div>
                                            <TextField
                                                variant="standard"
                                                onKeyDown={(ev) => addPrefix(index, ev)}
                                                style={{minWidth: "500px"}}
                                            />
                                        </TableCell>
                                        <TableCell style={{minWidth: "100px"}}>
                                            <Tooltip
                                                arrow={true}
                                                placement={"top"}
                                                title={
                                                    <FormattedMessage
                                                        id="privateRoutingDestinations.buttons.tooltips.delete"
                                                        defaultMessage="Delete destination"
                                                    />
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        disabled={!enabled}
                                                        color="secondary"
                                                        size="small"
                                                        onClick={() => removeDestination(index)}
                                                    >
                                                        <FontAwesomeIcon icon="trash" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {getError(index) && (
                                                <FontAwesomeIcon
                                                    title="Invalid destination name"
                                                    size={"2x"}
                                                    style={{color: "red", marginLeft: "15px"}}
                                                    icon="exclamation"
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className={classes.noMembersContainer}>
                        <FormattedAlert
                            message={defineMessage({
                                id: "privateRoutingDestinations.noDestinations",
                                defaultMessage: 'There are no destinations! Hit the "Add" to add.',
                            })}
                            severity="info"
                        />
                        <FormattedButton
                            message={defineMessage({
                                id: "privateRoutingDestinations.buttons.add",
                                defaultMessage: "Add",
                            })}
                            onClick={addNewDestination}
                            disabled={!enabled}
                            style={{marginLeft: "1em"}}
                        />
                    </div>
                )}
                <FormRowDivider />
                <FormattedFormRow>
                    <div style={{marginTop: "25px"}}>
                        <FormActionButtons
                            enabled={form.hasChanged && enabled}
                            mainAction={submit}
                            secondaryAction={() => {
                                form.reset();
                            }}
                        />
                    </div>
                </FormattedFormRow>
            </FormContainer>
        </Page>
    );
};

const PrivateRoutingDestination = () => {
    const dataSource = useHookClass(DataSource);

    if (!dataSource.loaded) return <Loading />;

    return <Content dataSource={dataSource} />;
};

export default PrivateRoutingDestination;
